import { useEffect, useState, type FormEvent } from 'react'

import { Button } from '#app/components/ui/button.tsx'
import { ResponsiveOverlay } from '#app/components/ui/responsive-overlay.tsx'

import { MONITORING_FEED_COPY } from '#app/routes/mocra+/_form+/components/monitoring-feeds/constants.ts'
import { MonitoringFeedForm } from '#app/routes/mocra+/_form+/components/monitoring-feeds/form.tsx'
import {
	collectMonitoringFeedErrors,
	createMonitoringFeedDraft,
	sanitizeMonitoringFeedDraft,
	type MonitoringFeedDraft,
} from '#app/routes/mocra+/_form+/components/monitoring-feeds/types.ts'

export type MonitoringFeedOverlayMode = 'add' | 'edit'

type DraftErrors = Partial<Record<keyof MonitoringFeedDraft, string>>

type Props = {
	readonly mode: MonitoringFeedOverlayMode
	readonly open: boolean
	readonly onOpenChange: (open: boolean) => void
	readonly onSubmit: (draft: MonitoringFeedDraft) => void
	readonly initialValue?: MonitoringFeedDraft
	readonly onCancel?: () => void
	readonly onClosed?: () => void
}

export function MonitoringFeedOverlay({
	mode,
	open,
	onOpenChange,
	onSubmit,
	initialValue,
	onCancel,
	onClosed,
}: Props) {
	const [draft, setDraft] = useState<MonitoringFeedDraft>(
		initialValue ? { ...initialValue } : createMonitoringFeedDraft(),
	)
	const [errors, setErrors] = useState<DraftErrors>({})

	useEffect(() => {
		if (open) {
			setDraft(initialValue ? { ...initialValue } : createMonitoringFeedDraft())
			setErrors({})
		}
	}, [initialValue, open])

	useEffect(() => {
		if (!open) {
			onClosed?.()
		}
	}, [onClosed, open])

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault()
		event.stopPropagation()
		const sanitized = sanitizeMonitoringFeedDraft(draft)
		const validationErrors = collectMonitoringFeedErrors(sanitized)
		if (validationErrors.length > 0) {
			const nextErrors: DraftErrors = {}
			nextErrors.detail = validationErrors[0]
			setErrors(nextErrors)
			return
		}
		onSubmit(sanitized)
	}

	const handleCancel = () => {
		onOpenChange(false)
		onCancel?.()
	}

	const titleCopy =
		mode === 'add'
			? MONITORING_FEED_COPY.title.add
			: MONITORING_FEED_COPY.title.edit
	const submitCopy =
		mode === 'add'
			? MONITORING_FEED_COPY.submit.add
			: MONITORING_FEED_COPY.submit.edit

	return (
		<ResponsiveOverlay
			open={open}
			onOpenChange={onOpenChange}
			title={titleCopy}
			description={MONITORING_FEED_COPY.description}
			className="sm:max-w-xl"
			footer={
				<div className="flex justify-end gap-3">
					<Button type="button" variant="outline" onClick={handleCancel}>
						Cancel
					</Button>
					<Button type="submit" form="monitoring-feed-overlay-form">
						{submitCopy}
					</Button>
				</div>
			}
		>
			<MonitoringFeedForm
				formId="monitoring-feed-overlay-form"
				draft={draft}
				errors={errors}
				onDraftFieldChange={(field, value) =>
					setDraft((prev) => ({ ...prev, [field]: value }))
				}
				onSubmit={handleSubmit}
			/>
		</ResponsiveOverlay>
	)
}
